import { z } from 'zod';
import { FeeStatus, StudyLevel, StudyMode } from '../enums.js';
import { EntryPoint } from '../offer/enums.js';
import { intakeIdValidator } from '../schemas/index.js';
import { AgentFormKeyNames, SharedFormKeyNames } from '../registration/registrationFormEnums.js';

// Define ProgrammeChoiceSchema locally to avoid circular dependency
export const ProgrammeChoiceSchema = z.object({
  intakeId: intakeIdValidator.min(1, 'Please select an intake'),
  intendedLevelOfStudy: z.nativeEnum(StudyLevel, { message: 'Please select a study level' }),
  courseName: z.string().min(1, { message: 'Please select a course name' }),
  mode: z.nativeEnum(StudyMode),
  campus: z.string().min(1),
  entryPoint: z.nativeEnum(EntryPoint),
  courseId: z.string().min(1, { message: 'Please select a course' }),
});

const MIN_PASSWORD_LENGTH = 8;
const MAX_PASSWORD_LENGTH = 256;

export const emailValidationSchema = z.string().trim().toLowerCase().email('Email is not valid.');

export const minLengthSchema = z
  .string()
  .min(MIN_PASSWORD_LENGTH, { message: 'Password must be at least 8 characters long.' });

export const maxLengthSchema = z
  .string()
  .max(MAX_PASSWORD_LENGTH, { message: 'Password must be maximum least 256 characters long.' });

export const letterSchema = z.string().regex(/[a-zA-Z]/, { message: 'Password must contain at least one letter.' });

export const digitSchema = z.string().regex(/\d/, { message: 'Password must contain at least one digit.' });

export const specialCharSchema = z.string().regex(/[=+\-^$*.[\]{}()?"!@#%&/,><\\':;|_~`]+/, {
  message: 'Password must contain at least one special character.',
});

export const noWhitespaceSchema = z
  .string()
  .regex(/^\S+$/, { message: 'Password must not start or end with whitespace.' });

export const passwordSchema = minLengthSchema
  .and(maxLengthSchema)
  .and(letterSchema)
  .and(digitSchema)
  .and(specialCharSchema)
  .and(noWhitespaceSchema);

export const ApplicantNewPasswordSchema = z.object({
  clientId: z.string(),
  email: emailValidationSchema,
  newPassword: z.string().min(MIN_PASSWORD_LENGTH),
  session: z.string(),
});

export const ApplicantRegistrationSchema = z.object({
  knownBy: z
    .string()
    .trim()
    .min(1)
    .max(50, 'Known by cannot exceed 50 characters.')
    .regex(
      /^[a-zA-Z\s'-]+$/,
      `Use letters A–Z only, plus spaces, hyphens (-) or apostrophes ('). Accents and special characters aren’t supported`,
    ),
  email: emailValidationSchema,
  marketing: z.boolean(),
  referrer: z.string(),
});

export const ApplicantLoginSchema = z.object({
  email: emailValidationSchema,
  password: passwordSchema,
});

export const ApplicantChangePasswordSchema = ApplicantLoginSchema.extend({
  code: z.string().optional(),
});

export const StudentAuthoriseAgentSchema = z.object({
  studentAuthoriseAgent: z.coerce.boolean(),
  marketingConsent: z.boolean().optional(),
});

// Define the schema for agent registration
export const BasicApplicantExternalRegistrationSchema = z.object({
  [SharedFormKeyNames.EMAIL]: z.string().email('Email is not valid.').max(128, 'Email cannot exceed 128 characters.'),
  [SharedFormKeyNames.DISABLE_EMAIL_NOTIFICATIONS]: z.boolean().optional(),
  [SharedFormKeyNames.PREFERRED_NAME]: z
    .string()
    .regex(
      /^[a-zA-Z\s'-]+$/,
      `Use letters A–Z only, plus spaces, hyphens (-) or apostrophes ('). Accents and special characters aren’t supported`,
    )
    .min(1, 'Preferred name is required')
    .max(50, 'Preferred name cannot exceed 50 characters.'),
  [SharedFormKeyNames.UNIVERSITY_ID]: z.string().min(2, 'Institution is required'),
  [SharedFormKeyNames.PROGRAMME_CHOICES]: z
    .array(ProgrammeChoiceSchema)
    .length(1, 'You must select exactly one programme choice to proceed'),
  [SharedFormKeyNames.FEE_STATUS]: z.nativeEnum(FeeStatus, { message: 'Fee status is required' }),
  [SharedFormKeyNames.NATIONALITY]: z.string().min(3, 'Nationality is required'),
  [SharedFormKeyNames.DOB]: z
    .string()
    .min(1, 'Date of birth is required')
    .refine(
      val => {
        const [YYYY, MM, DD] = val.split('-');
        return (
          DD &&
          MM &&
          YYYY &&
          DD.length === 2 &&
          MM.length === 2 &&
          YYYY.length === 4 &&
          parseInt(DD) <= 31 &&
          parseInt(MM) <= 12 &&
          parseInt(YYYY) > 1900
        );
      },
      { message: 'Date of birth is incomplete or invalid.' },
    )
    .refine(
      val => {
        const now = new Date();
        const dob = new Date(val);
        return dob < now && dob.toDateString() !== now.toDateString();
      },
      { message: 'Date of birth cannot be in the future or today' },
    )
    .refine(
      val => {
        const [year, month, day] = val.split('-').map(Number);
        const today = new Date();
        const todayMonth = today.getMonth() + 1;
        const age =
          today.getFullYear() - year - (todayMonth < month || (todayMonth === month && today.getDate() < day) ? 1 : 0);
        return age > 13;
      },
      { message: 'Must be older than 13 years old.' },
    ),
  [SharedFormKeyNames.MOBILE_NUMBER]: z
    .string()
    .min(1, 'Phone number is required.')
    .min(2, 'Invalid phone number length.')
    .regex(/^\d+(-\d+)*$/, 'Invalid phone number.')
    .refine((value: string) => value.includes('-'), { message: 'Phone number must include country code.' })
    .refine(
      (value: string) => {
        const [code, phone] = value.split('-');
        return code && phone && code.length && phone.length && /^\d+$/.test(code) && /^\d+$/.test(phone);
      },
      { message: 'Invalid phone number.' },
    ),
});

export const AgentRegistrationSchema = BasicApplicantExternalRegistrationSchema.extend({
  [AgentFormKeyNames.AGENT_CONSENT]: z.boolean().refine(val => val === true, {
    message: 'Agent consent is required',
  }),
});

export const UniRegistrationSchema = BasicApplicantExternalRegistrationSchema;

export const ProviderRegistrationSchema = BasicApplicantExternalRegistrationSchema;

export const UniStaffRegistrationSchema = BasicApplicantExternalRegistrationSchema;

/* eslint-disable @typescript-eslint/no-empty-object-type */
export interface IApplicantRegistration extends z.infer<typeof ApplicantRegistrationSchema> {}

export interface IApplicantLogin extends z.infer<typeof ApplicantLoginSchema> {}

export interface IApplicantNewPassword extends z.infer<typeof ApplicantNewPasswordSchema> {}

export interface IApplicantChangePassword extends z.infer<typeof ApplicantChangePasswordSchema> {}

export interface IStudentAuthoriseAgentSchema extends z.infer<typeof StudentAuthoriseAgentSchema> {}

export interface IAgentRegistration extends z.infer<typeof AgentRegistrationSchema> {}
export interface IProviderRegistration extends z.infer<typeof ProviderRegistrationSchema> {}
export interface IUniStaffRegistration extends z.infer<typeof UniStaffRegistrationSchema> {}
